// The export of the view of a kit DataTable (SEP-298).
//
// An export of the view holds what the reader sees: the visible columns, in
// the order of the reader, with the header text of the table, and every row
// that passes the filters, in the current sort. The page does not limit it.
// CSV and XLSX hold the same headers, the same order and the same values.
// A number stays a number, so a spreadsheet can add it up.
// The builders are pure, so the rules are unit-tested here.

import type { ReactNode } from 'react';
import type { ExportFormat } from '@/lib/download';
import { downloadBlob } from '@/lib/download';
import type { FilterScalar } from './data-table-filter';

export type TableViewValue = string | number | boolean | null;

export interface TableView {
    headers: string[];
    rows: TableViewValue[][];
}

/** The part of a DataTable column that an export reads. */
export interface ExportColumn<T> {
    id: string;
    header: ReactNode;
    cell: (row: T) => ReactNode;
    exportValue?: (row: T) => TableViewValue;
    filterValue?: (row: T) => FilterScalar;
    filterLabel?: (row: T) => string;
    sortValue?: (row: T) => string | number | null;
    enumOptions?: readonly { value: string; label: string }[];
}

function fromScalar(value: FilterScalar | undefined): TableViewValue {
    if (value === undefined || value === null) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : isoDay(value);
    return value;
}

// A date that the filter reads is a day. The export writes it as an ISO date,
// which each spreadsheet reads as a date.
function isoDay(date: Date): string {
    return date.toISOString().slice(0, 10);
}

/** The value of one cell in the export. */
export function exportCellValue<T>(col: ExportColumn<T>, row: T): TableViewValue {
    if (col.exportValue) return col.exportValue(row);
    // A closed set of values: the export holds the label that the reader sees.
    if (col.enumOptions && (col.filterValue || col.sortValue)) {
        const raw = col.filterValue ? col.filterValue(row) : col.sortValue!(row);
        const option = col.enumOptions.find((o) => o.value === raw);
        if (option) return option.label;
    }
    if (col.filterLabel) {
        const text = col.filterLabel(row);
        if (text !== '') return text;
    }
    if (col.filterValue) return fromScalar(col.filterValue(row));
    if (col.sortValue) return fromScalar(col.sortValue(row));
    const cell = col.cell(row);
    if (typeof cell === 'string' || typeof cell === 'number') return cell;
    return null;
}

export function buildTableView<T>(
    columns: readonly ExportColumn<T>[],
    rows: readonly T[],
    headerLabel: (col: ExportColumn<T>) => string,
): TableView {
    return {
        headers: columns.map((c) => headerLabel(c)),
        rows: rows.map((row) => columns.map((c) => exportCellValue(c, row))),
    };
}

// The same escape as the server CSV writer (`modules/export/tabular.ts`), so
// an export of the view and an export of all records read the same way.
function csvEscape(v: TableViewValue): string {
    const s = v === null ? '' : String(v);
    if (s.includes(',') || s.includes('"') || s.includes('\n') || s.includes('\r')) {
        return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
}

export function tableViewToCsv(view: TableView): string {
    return [view.headers, ...view.rows].map((line) => line.map(csvEscape).join(',')).join('\r\n');
}

const XLSX_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * Download the view of a table. The XLSX writer loads only when a reader
 * asks for a workbook, so the table code does not carry it.
 */
export async function exportTableView(
    view: TableView | null | undefined,
    baseName: string,
    format: ExportFormat,
): Promise<void> {
    if (!view) throw new Error('The table is not ready to export');
    if (format === 'csv') {
        // A byte-order mark makes Excel read the file as UTF-8 (a £ sign).
        downloadBlob(
            new Blob(['﻿', tableViewToCsv(view)], { type: 'text/csv;charset=utf-8' }),
            `${baseName}.csv`,
        );
        return;
    }
    const XLSX = await import('xlsx');
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.aoa_to_sheet([view.headers, ...view.rows]),
        'Data',
    );
    const bytes = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' }) as ArrayBuffer;
    downloadBlob(new Blob([bytes], { type: XLSX_TYPE }), `${baseName}.xlsx`);
}
